import React, { useMemo } from "react";
import VStack from "../../ui_atoms/VStack";
import { MockedProvider } from "@apollo/client/testing";
import type { Meta, StoryObj } from "@storybook/react";
import { createStackNavigator } from "@react-navigation/stack";
import WorkerInvitationDetailsScreenForClient from "../../screens/user/WorkerInvitationDetailsForClient";
import PostOnboardingProfileEditorScreen from "../../screens/profile-editor/PostOnboardingProfileEditor";
import { ProfileType } from "../../__generated__/graphql";
import { buildClientProfile, buildInvitation, buildUser, buildWorkerProfile } from "../factories";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { ACCEPT_INVITATION, GET_INVITATION, REJECT_INVITATION } from "../../graphql/invitation";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { Alert } from "react-native";
import { CREATE_WORKER } from "../../graphql/core";

const Stack = createStackNavigator<{
    WorkerInvitationDetailsScreenForClient: undefined;
    PostOnboardingProfileEditor: { newProfileId: string };
}>();

const StoryComponent = ({ workerProfileId }: { workerProfileId: string }) => {
    const mocks = [
        {
            request: {
                query: GET_INVITATION,
            },
            variableMatcher: () => true,
            newData: () => {
                return {
                    data: {
                        getInvitation: buildInvitation(),
                    },
                };
            },
        },
        {
            request: {
                query: ACCEPT_INVITATION,
            },
            variableMatcher: () => true,
            newData: () => {
                return {
                    data: {},
                };
            },
        },
        {
            request: {
                query: REJECT_INVITATION,
            },
            variableMatcher: () => true,
            newData: () => {
                return {
                    data: {},
                };
            },
        },
        {
            request: {
                query: CREATE_WORKER,
            },
            variableMatcher: () => true,
            newData: () => {
                return {
                    data: {
                        createWorkerProfile: {
                            id: "1",
                        },
                    },
                };
            },
        },
    ];
    const user = useMemo(
        () =>
            buildUser({
                email: workerProfileId === "completed" ? "test@test.com" : null,
                clientProfiles: [buildClientProfile()],
                workerProfile: workerProfileId !== "none" ? buildWorkerProfile() : null,
            }),
        [workerProfileId],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager
                profileType={ProfileType.Client}
                setProfile={(profile) => {
                    Alert.alert(`Change profile "${profile?.id}"`);
                }}
            >
                <MockedProvider mocks={mocks}>
                    <VStack
                        gap={1}
                        style={{ alignItems: "stretch", alignSelf: "stretch", flex: 1 }}
                    >
                        <Stack.Navigator initialRouteName="WorkerInvitationDetailsScreenForClient">
                            <Stack.Screen
                                name="WorkerInvitationDetailsScreenForClient"
                                // @ts-expect-error - navigation prop is not used in the story
                                component={WorkerInvitationDetailsScreenForClient}
                                options={{ headerShown: true, title: "Invitation" }}
                            />
                            <Stack.Screen
                                name="PostOnboardingProfileEditor"
                                // @ts-expect-error - navigation prop is not used in the story
                                component={PostOnboardingProfileEditorScreen}
                            />
                        </Stack.Navigator>
                    </VStack>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Invitation worker (client side)",
    component: StoryComponent,
    argTypes: {
        workerProfileId: {
            control: {
                type: "select",
                options: ["none", "uncompleted", "completed"],
            },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const NoWorkerProfile: Story = {
    args: {
        workerProfileId: "none",
    },
};

export const WithWorkerProfile: Story = {
    args: {
        workerProfileId: "uncompleted",
    },
};

export const WithWorkerProfileCompleted: Story = {
    args: {
        workerProfileId: "completed",
    },
};
